import React, { useState, useEffect } from "react";
import { auth, storage } from "../firebase";
import { onAuthStateChanged, updateProfile } from "firebase/auth";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import PageHeader from "./PageHeader";

const ProfilePage = () => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [loading, setLoading] = useState(true);

  // Fetch user data and dynamically update the state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setName(currentUser.displayName || "");
        setEmail(currentUser.email || "");
        setPhotoURL(
          currentUser.photoURL?.replace("s96-c", "s400-c") || "https://via.placeholder.com/150"
        );
      } else {
        console.warn("No user found.");
        setPhotoURL("https://via.placeholder.com/150");
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Handle updating the profile picture
  const handleProfilePictureChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const storageRef = ref(storage, `profilePictures/${auth.currentUser.uid}`);
        await uploadBytes(storageRef, file);

        const newPhotoURL = await getDownloadURL(storageRef);
        await updateProfile(auth.currentUser, { photoURL: newPhotoURL });

        setPhotoURL(newPhotoURL); // Update the local state
        alert("Profile picture updated!");
      } catch (error) {
        console.error("Error updating profile picture:", error);
        alert("Failed to update profile picture. Please try again.");
      }
    }
  };

  // Save the updated profile name
  const handleSave = async () => {
    try {
      await updateProfile(auth.currentUser, { displayName: name });
      alert("Profile saved!");
    } catch (error) {
      console.error("Error saving profile:", error);
      alert("Failed to save profile. Please try again.");
    }
  };

  return (
    <>
      <PageHeader title="Profile" />
      <div style={styles.container}>
        {/* Skeleton Loader while data loads */}
        {loading ? (
          <>
            <div style={styles.skeletonProfilePicture}></div>
            <div style={styles.skeletonInput}></div>
            <div style={styles.skeletonInput}></div>
            <div style={styles.skeletonButton}></div>
            <div style={styles.skeletonButton}></div>
          </>
        ) : (
          <>
            {/* Profile Picture */}
            <div style={styles.profilePictureContainer}>
              <label htmlFor="profilePictureInput">
                <img
                  src={photoURL}
                  alt="Profile"
                  style={styles.profilePicture}
                  title="Click to change profile picture"
                />
              </label>
              <input
                id="profilePictureInput"
                type="file"
                accept="image/*"
                style={{ display: "none" }}
                onChange={handleProfilePictureChange}
              />
            </div>

            {/* Editable Fields */}
            <div style={styles.details}>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Name"
                style={styles.input}
              />
              <input
                type="email"
                value={email}
                readOnly
                style={styles.input}
              />
              <button onClick={handleSave} style={styles.saveButton}>
                Save
              </button>
              <button onClick={() => auth.signOut()} style={styles.logoutButton}>
                Logout
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
};

const styles = {
  container: {
    padding: "20px",
    fontFamily: "'Arial', sans-serif",
    maxWidth: "600px",
    margin: "0 auto",
    textAlign: "center",
  },
  profilePictureContainer: {
    marginBottom: "20px",
  },
  profilePicture: {
    width: "150px",
    height: "150px",
    borderRadius: "50%",
    objectFit: "cover",
    cursor: "pointer",
  },
  details: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  input: {
    width: "100%",
    padding: "10px",
    marginBottom: "10px",
    border: "1px solid #ddd",
    borderRadius: "5px",
    fontSize: "14px",
  },
  saveButton: {
    width: "100%",
    padding: "10px",
    fontSize: "16px",
    backgroundColor: "black",
    color: "white",
    border: "none",
    borderRadius: "5px",
    cursor: "pointer",
    marginBottom: "10px",
  },
  logoutButton: {
    width: "100%",
    padding: "10px",
    fontSize: "16px",
    backgroundColor: "#dc3545",
    color: "white",
    border: "none",
    borderRadius: "5px",
  },
  // Skeleton Loader Styles
  skeletonProfilePicture: {
    width: "150px",
    height: "150px",
    borderRadius: "50%",
    backgroundColor: "#e0e0e0",
    marginBottom: "20px",
  },
  skeletonInput: {
    width: "100%",
    height: "40px",
    backgroundColor: "#e0e0e0",
    marginBottom: "10px",
    borderRadius: "5px",
  },
  skeletonButton: {
    width: "100%",
    height: "40px",
    backgroundColor: "#e0e0e0",
    marginBottom: "10px",
    borderRadius: "5px",
  },
};

export default ProfilePage;
